import { useCallback, useEffect, useRef, useState } from "react";
import { UserFlagsDto } from "@repo/api/user";
import { useSelfQuery, useSetUserFlagsMutation } from "../queries/user-queries";

export type OneTimeHintFlagKey = keyof UserFlagsDto;

const DEFAULT_DELAY_MS = 2000;

export function useOneTimeHint(
  flagKey: OneTimeHintFlagKey,
  delayMs: number = DEFAULT_DELAY_MS,
) {
  const { data: self } = useSelfQuery();
  const setUserFlags = useSetUserFlagsMutation();
  const dismissedRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!self || dismissedRef.current) {
      return;
    }

    if (self.flags[flagKey] !== undefined) {
      return;
    }

    const timer = setTimeout(() => {
      if (!dismissedRef.current) {
        setIsOpen(true);
      }
    }, delayMs);

    return () => clearTimeout(timer);
  }, [self, flagKey, delayMs]);

  const dismiss = useCallback(() => {
    if (dismissedRef.current) {
      return;
    }
    dismissedRef.current = true;
    setIsOpen(false);

    if (self && self.flags[flagKey] === undefined) {
      setUserFlags.mutate({ [flagKey]: new Date().toISOString() });
    }
  }, [self, flagKey, setUserFlags]);

  return { isOpen, dismiss };
}
